import type { FC } from "react";

import { useCheckoutConfiguration } from "@jti/commerce/providers";
import { DOB_INPUT_DATE } from "@jti/fe-common";
import { convertStoredAddressToCasAddressStored, getUser } from "@jti/rest-api";
import { useDispatch, useSelector } from "@jti/store/reduxHooks";
import { selectAccountContact } from "@jti/store/selectors/selectAccountContact";
import { selectAddresses } from "@jti/store/selectors/selectAddresses";
import { updateAgeVerification } from "@jti/store/slices/LEGACY__checkoutSlice";
import { AgeVerificationBanner, Form, formatDate, useForm } from "@jti/ui";

import { CartItems } from "@checkout/components/CartItems";

type CartPageContentProps = {
  isCartItemsLimitReached: boolean;
  isAgeVerificationErrorVisible: boolean;
};

type FormValues = {
  avpValues: Record<string, string>;
};

export const CartPageContent: FC<CartPageContentProps> = ({
  isCartItemsLimitReached = false,
  isAgeVerificationErrorVisible,
}) => {
  const dispatch = useDispatch();
  const user = getUser();

  const { ageVerificationPopup, ageVerificationMethodImages } = useCheckoutConfiguration();

  const accountContactData = useSelector(selectAccountContact);
  const accountAddresses = useSelector(selectAddresses);
  const isLimitEnabled = useSelector((state) => state.cart.storeConfig?.is_order_limit_enabled);
  const profileDataFetchStatus = useSelector((state) => state.checkout.profileDataFetchStatus);
  const isAgeVerificationSuccess = useSelector((state) => state.checkout.isAgeVerificationSuccess);
  const updateAgeVerificationPending = useSelector((state) => state.checkout.updateAgeVerificationPending);

  const form = useForm<FormValues>({
    defaultValues: {},
  });

  const onSuccess = async (avpUuid: string) => {
    dispatch(updateAgeVerification({ avpUuid }));
  };

  const preparedAddresses = accountAddresses.map(convertStoredAddressToCasAddressStored);
  const showAgeVerificationBanner = isAgeVerificationErrorVisible || isAgeVerificationSuccess;

  return (
    <CartItems
      isLimitEnabled={isLimitEnabled}
      warningStatus={isCartItemsLimitReached}
      ageVerificationMessage={
        showAgeVerificationBanner && (
          <Form<FormValues> form={form} onSubmit={() => null} dataTestId="ageVerificationForm">
            <AgeVerificationBanner
              isPending={updateAgeVerificationPending || profileDataFetchStatus === "pending"}
              dialogDescription={ageVerificationPopup?.description || ""}
              onSuccess={onSuccess}
              isAgeVerificationSuccess={isAgeVerificationSuccess}
              ageVerificationMethodImages={ageVerificationMethodImages || {}}
              mode={ageVerificationPopup?.popupMode ? "popup" : "link"}
              successMessage={ageVerificationPopup?.successMessage || ""}
              userData={{
                firstname: accountContactData?.firstname,
                lastname: accountContactData?.lastname,
                phone: accountContactData?.telephone,
                email: user.email,
                dateOfBirth: formatDate(accountContactData?.dob, DOB_INPUT_DATE),
                addresses: preparedAddresses,
              }}
            />
          </Form>
        )
      }
    />
  );
};
